import React, { Component } from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import ReactDOM from 'react-dom';

export type ImageGenModel = 'SDXL' | 'Flux1.Dev' | 'Flux.Kontext' | 'Flux1.Schnell' | 'Flux2.Dev' | 'Flux2.Klein' | 'zImage' | 'qwen';
type ImageGenModelType = 'SDXL' | 'Flux' | 'zImage' | 'Qwen';

type ImageModelPickerProps = {
  model: ImageGenModel;
  onSelect: (model: ImageGenModel) => void;
  hasImages?: boolean;
};

const DropdownMenu = ({ 
  models, 
  currentModel, 
  onSelect, 
  getDisplayName,
  disabledModels,
  rect,
  isClosing,
  onAnimationEnd,
  dropdownRef,
}: { 
  models: ImageGenModel[], 
  currentModel: ImageGenModel, 
  onSelect: (model: ImageGenModel) => void,
  getDisplayName: (model: ImageGenModel) => string,
  disabledModels?: Set<ImageGenModel>,
  rect: { left: number; width: number; bottom: number } | null,
  isClosing: boolean,
  onAnimationEnd: () => void,
  dropdownRef: React.RefObject<HTMLDivElement | null>,
}) => {
  return ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      className={'image-gen-model-dropdown' + (isClosing ? ' closing' : '')}
      onAnimationEnd={onAnimationEnd}
      style={{
        position: 'fixed',
        left: rect ? rect.left + rect.width / 2 : 0,
        bottom: rect ? rect.bottom : 0,
        transform: 'translateX(-50%)',
      }}
    >
      {models.map((m) => {
        const isDisabled = disabledModels?.has(m);
        return (
          <div
            key={m}
            className={`dropdown-item${currentModel === m ? ' active' : ''}${isDisabled ? ' disabled' : ''}`}
            onClick={isDisabled ? undefined : () => onSelect(m)}
          >
            <span className="dropdown-item-text">{getDisplayName(m)}</span>
            {currentModel === m && <span className="dropdown-check">✓</span>}
          </div>
        );
      })}
    </div>,
    document.body
  );
};

@observer
class ImageModelPicker extends Component<ImageModelPickerProps> {
  @observable openDropdown: string | null = null;
  @observable isClosing: boolean = false;
  @observable dropdownRect: { left: number; width: number; bottom: number } | null = null;

  dropdownRef = React.createRef<HTMLDivElement>();
  fluxBtnRef = React.createRef<HTMLDivElement>();

  constructor(props: ImageModelPickerProps) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  @action
  private handleClickOutside = (event: MouseEvent) => {
    const inWrapper = this.dropdownRef.current?.contains(event.target as Node);
    const inPortal = (event.target as HTMLElement).closest('.image-gen-model-dropdown') !== null;
    if (!inWrapper && !inPortal && this.openDropdown && !this.isClosing) {
      this.isClosing = true;
    }
  };

  private getFluxModels(): ImageGenModel[] {
    return [
      'Flux1.Dev',
      'Flux.Kontext',
      'Flux2.Dev',
      'Flux2.Klein',
    ];
  }

  private isFluxModel(model: ImageGenModel): boolean {
    return this.getFluxModels().includes(model);
  }

  @action
  private toggleDropdown = (modelName: string, anchorEl: HTMLElement | null) => {
    if (this.openDropdown === modelName) {
      this.isClosing = true;
      return;
    }
    let rect: { left: number; width: number; bottom: number } | null = null;
    if (anchorEl) {
      const r = anchorEl.getBoundingClientRect();
      rect = { left: r.left, width: r.width, bottom: window.innerHeight - r.top + 8 };
    }
    this.openDropdown = modelName;
    this.isClosing = false;
    this.dropdownRect = rect;
  };

  @action
  private handleModelClick = (m: { name: ImageGenModelType }, isFlux: boolean, isDropdownOpen: boolean, isDisabled: boolean) => {
    if (isDisabled) return;
    if (isFlux) {
      this.isClosing = isDropdownOpen;
      if (!isDropdownOpen) this.openDropdown = null;
      this.props.onSelect('Flux2.Klein' as ImageGenModel);
    } else {
      this.openDropdown = null;
      this.isClosing = false;
      if (m.name === 'Qwen') {
        this.props.onSelect('qwen' as ImageGenModel);
      } else {
        this.props.onSelect(m.name as ImageGenModel);
      }
    }
  };

  @action
  private handleDropdownModelSelect = (selectedModel: ImageGenModel) => {
    this.props.onSelect(selectedModel);
    this.isClosing = true;
  };

  @action
  private handleDropdownAnimationEnd = () => {
    if (this.isClosing) {
      this.openDropdown = null;
      this.isClosing = false;
    }
  };

  getDisplayName = (model: ImageGenModel): string => {
    const mapping: Record<string, string> = {
      'Flux1.Dev': 'Flux.1 Dev',
      'Flux.Kontext': 'Flux Kontext',
      'Flux1.Schnell': 'Flux.1 Schnell',
      'Flux2.Dev': 'Flux.2 Dev',
      'Flux2.Klein': 'Flux.2 Klein',
      'qwen': 'Qwen',
    };
    return mapping[model] || model;
  };

  render() {
    const { model } = this.props;
    const openDropdown = this.openDropdown;
    const isClosing = this.isClosing;
    const dropdownRect = this.dropdownRect;
    const hasImages = !!this.props.hasImages;
    
    const models: { name: ImageGenModelType, displayName?: string, previewUrl: string }[] = [
      { name: 'zImage', previewUrl: '/img/models/zimage-preview.jpg' },
      { name: 'SDXL', previewUrl: '/img/models/sdxl-preview.jpg' },
      { name: 'Flux', previewUrl: '/img/models/flux-preview.jpg' },
      { name: 'Qwen', previewUrl: '/img/models/qwen-image-edit-preview.jpg' }
    ];

    return (
      <>
        <div className="image-gen-controls-model" ref={this.dropdownRef}>
          <div className="control-group">
            <label>Model:</label>
            <div className="image-model-selector">
              {models.map((m) => {
                const isFluxActive = m.name === 'Flux' && this.isFluxModel(model);
                const isQwenActive = m.name === 'Qwen' && model === 'qwen';
                const isActive = model === m.name || isFluxActive || isQwenActive;
                const isDropdownOpen = openDropdown === m.name;
                const currentSelection = isFluxActive ? this.getDisplayName(model) : '';
                const isFlux = m.name === 'Flux';
                // Disable t2i-only models when images are attached
                const isDisabled = hasImages && (m.name === 'zImage' || m.name === 'SDXL');
                
                return (
                  <div key={m.name} className="model-btn-wrapper" ref={m.name === 'Flux' ? this.fluxBtnRef : undefined}>
                    <button
                      className={`model-btn${isActive ? ' active' : ''}${isDropdownOpen ? ' dropdown-open' : ''}${isDisabled ? ' disabled' : ''}`}
                      disabled={isDisabled}
                      onClick={() => {
                        this.handleModelClick(m, isFlux, isDropdownOpen, isDisabled);
                      }}
                      title={isDisabled ? `${m.name} does not support image-to-image` : m.name}
                    >
                      <img loading="lazy" src={m.previewUrl} alt={m.name} className="model-preview" />
                      <div
                        className="model-name-row"
                        onClick={isFlux ? (e) => { e.stopPropagation(); this.toggleDropdown('Flux', this.fluxBtnRef.current); } : undefined}
                      >
                        <span>{m.displayName || m.name}</span>
                        {isFlux && <span className="dropdown-icon">▼</span>}
                      </div>
                      {isFlux && isActive && currentSelection && (
                        <div className="selected-model">{currentSelection}</div>
                      )}
                    </button>
                    
                    {isFlux && openDropdown === 'Flux' && (
                      <DropdownMenu
                        models={this.getFluxModels()}
                        currentModel={model}
                        onSelect={(selectedModel) => {
                          this.handleDropdownModelSelect(selectedModel);
                        }}
                        getDisplayName={this.getDisplayName}
                        disabledModels={hasImages ? new Set<ImageGenModel>(['Flux1.Dev']) : undefined}
                        rect={dropdownRect}
                        isClosing={isClosing}
                        onAnimationEnd={() => {
                          this.handleDropdownAnimationEnd();
                        }}
                        dropdownRef={this.dropdownRef}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default ImageModelPicker;